import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import type {
  BouquetBuildSummary,
  CreateBouquetBuildPayload,
  DefaultUom,
  Location,
} from "../../api/types";
import { ItemPicker } from "../ItemPicker";
import { Modal } from "../Modal";

const UOMS: DefaultUom[] = ["STEM", "UNIT", "CM", "ROLL"];

type DraftLine = { uid: string; itemId: string; quantity: string; uom: DefaultUom };

interface Props {
  location: Location;
  onClose: () => void;
  onBuilt: (build: BouquetBuildSummary) => void;
}

const newUid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function BuildBouquetForSaleModal({ location, onClose, onBuilt }: Props) {
  const defaultZoneId = useMemo(() => {
    const front = location.zones.find((z) => z.zoneType === "FRONT_SHOP");
    return front?.id ?? location.zones[0]?.id ?? "";
  }, [location]);

  const [zoneId, setZoneId] = useState(defaultZoneId);
  const [lines, setLines] = useState<DraftLine[]>([
    { uid: newUid(), itemId: "", quantity: "1", uom: "STEM" },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!zoneId && defaultZoneId) setZoneId(defaultZoneId);
  }, [zoneId, defaultZoneId]);

  const updateLine = (uid: string, patch: Partial<DraftLine>) =>
    setLines((all) => all.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));

  const addLine = () =>
    setLines((all) => [
      ...all,
      { uid: newUid(), itemId: "", quantity: "1", uom: "STEM" },
    ]);

  const removeLine = (uid: string) =>
    setLines((all) => (all.length <= 1 ? all : all.filter((l) => l.uid !== uid)));

  const buildAndAttach = async () => {
    setBusy(true);
    setError(null);
    let draftId: string | null = null;
    try {
      if (!zoneId) throw new Error("Pick a zone for the bouquet to live in");
      if (lines.length === 0) throw new Error("Add at least one flower line");

      const payloadLines = lines.map((l) => {
        const q = Number(l.quantity);
        if (!l.itemId || !Number.isFinite(q) || q <= 0) {
          throw new Error("Every line needs a flower and a positive quantity");
        }
        return { itemId: l.itemId, quantity: q, uom: l.uom };
      });

      const payload: CreateBouquetBuildPayload = {
        locationId: location.id,
        zoneId,
        lines: payloadLines,
      };

      const draft = await api.createBouquetBuild(payload);
      draftId = draft.id;
      const finalized = await api.finalizeBouquetBuild(draft.id);
      onBuilt(finalized);
    } catch (err: unknown) {
      // If we created a draft but finalize failed, void the draft so it doesn't linger.
      if (draftId) {
        await api.voidBouquetBuild(draftId).catch(() => {
          /* ignore — leaving the draft is harmless and the user can void it later */
        });
      }
      setError(err instanceof Error ? err.message : "Could not build the bouquet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Build a custom bouquet" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          We&apos;ll consume the chosen flowers FIFO from the selected zone, finalize the bouquet,
          and attach it to this sale. If a flower is short the build is rolled back automatically.
        </p>

        {error ? <div className="alert error">{error}</div> : null}

        <div className="form-grid">
          <div className="field">
            <label>Shop</label>
            <input value={`${location.name} (${location.code})`} readOnly />
          </div>
          <div className="field">
            <label htmlFor="custom-bouquet-zone">Zone *</label>
            <select
              id="custom-bouquet-zone"
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
            >
              {location.zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} ({z.zoneType})
                </option>
              ))}
            </select>
            <small className="hint">FIFO consumption pulls from this zone.</small>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {lines.map((line, idx) => (
            <div
              key={line.uid}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 100px 110px auto",
                gap: "var(--space-3)",
                alignItems: "flex-end",
                padding: "var(--space-2) var(--space-3)",
                borderRadius: "var(--radius-md)",
                background: "var(--color-surface-muted)",
              }}
            >
              <ItemPicker
                label={`Flower ${idx + 1}`}
                value={line.itemId}
                onChange={(v) => updateLine(line.uid, { itemId: v })}
                filter={(item) => item.category.code === "FLOWER"}
              />
              <div className="field">
                <label>Qty</label>
                <input
                  type="number"
                  min={0.001}
                  step="0.001"
                  value={line.quantity}
                  onChange={(e) => updateLine(line.uid, { quantity: e.target.value })}
                />
              </div>
              <div className="field">
                <label>UoM</label>
                <select
                  value={line.uom}
                  onChange={(e) =>
                    updateLine(line.uid, { uom: e.target.value as DefaultUom })
                  }
                >
                  {UOMS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="button plain"
                onClick={() => removeLine(line.uid)}
                disabled={lines.length <= 1}
                aria-label="Remove flower line"
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="button tinted"
            onClick={addLine}
            style={{ alignSelf: "flex-start" }}
          >
            + Add flower
          </button>
        </div>

        <div
          className="button-row"
          style={{ justifyContent: "flex-end", marginTop: "var(--space-2)" }}
        >
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void buildAndAttach()}
            disabled={busy || !zoneId}
          >
            {busy ? "Building…" : "Build & attach"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
